// Wariant CartsButton z fixem race condition na anchorEl Popovera.
// Trzyma referencje do DOM-a przez useRef i odswieza ja przy kazdym otwarciu.
// Guard isConnected zapobiega pozycjonowaniu w prawy dolny rog gdy anchor zostal odmontowany.

import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import classnames from 'classnames';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Cart, ChevronDown, ChevronLeft, ChevronRight } from 'react-bootstrap-icons';

import { reduxActions, useDispatch, useSelector } from 'store';
import { useGetCartsAll, usePostCartFromLastOrder } from 'api';
import { Availability, Link, Modal } from 'components/controls';
import { CartPositions } from 'components/containers';
import { useRWD } from 'hooks';

import styles from 'theme/components/wise/WiseCartsButton/WiseCartsButton.module.scss';

const WiseCartsButton = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useRWD();
  const { pathname } = useLocation();

  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLDivElement | null>(null);

  const { currentCartId } = useSelector((state) => state.cart);

  const {
    data: cartsData,
    refetch: refetchCartsData,
    isFetching: isCartsFetching
  } = useGetCartsAll({
    onSuccess: (data) => {
      !currentCartId && dispatch(reduxActions.setCurrentCartId(data.items[0]?.id) || null);
    }
  });

  const { mutate: createCartFromLastOrder, isLoading: isCreatingFromLastOrder } =
    usePostCartFromLastOrder({
      onSuccess: (data) => {
        dispatch(reduxActions.setCurrentCartId(data.data.id));
        refetchCartsData();
      }
    });

  const currentCart = useMemo(
    () => cartsData?.items.find((cart) => currentCartId === cart.id),
    [currentCartId, cartsData]
  );

  const currentCartIndex = useMemo(
    () => cartsData?.items.findIndex((cart) => currentCartId === cart.id),
    [currentCartId, cartsData]
  );

  const prevCart = () => {
    const prevCartId =
      typeof currentCartIndex === 'number' && cartsData?.items[currentCartIndex - 1]?.id;
    typeof prevCartId === 'number' && dispatch(reduxActions.setCurrentCartId(prevCartId));
  };

  const nextCart = () => {
    const nextCartId =
      typeof currentCartIndex === 'number' && cartsData?.items[currentCartIndex + 1]?.id;
    typeof nextCartId === 'number' && dispatch(reduxActions.setCurrentCartId(nextCartId));
  };

  // Ustawienie domyslnego koszyka po pobraniu/aktualizacji listy koszykow.
  // UWAGA: tylko gdy currentCartId jeszcze nie jest ustawiony. Inaczej, gdy URL
  // wskazuje na swiezo utworzony koszyk (np. po fulfill oferty), ktorego nie ma
  // jeszcze w cartsData, wpadalismy w petle: WiseCart ustawia cartId z URL,
  // WiseCartsButton nadpisuje go pierwszym z listy, WiseCart przywraca - i tak
  // do MRT_TableContainer "Maximum update depth exceeded".
  useEffect(() => {
    if (isCartsFetching) return;
    if (currentCartId) return;
    const fallbackId = cartsData?.items[0]?.id || null;
    if (fallbackId !== null) {
      dispatch(reduxActions.setCurrentCartId(fallbackId));
    }
  }, [isCartsFetching, currentCartId, cartsData]);

  // Otwarcie popovera - zawsze bierzemy aktualna referencje z ref, a nie z event.currentTarget.
  // Jesli element wciaz jest w DOM (.isConnected), uzywamy go jako anchor.
  const handleOpen = useCallback(() => {
    if (buttonRef.current?.isConnected) {
      setPopoverAnchor(buttonRef.current);
    }
  }, []);

  const handleClose = useCallback(() => {
    // flushSync wymusza synchroniczne zamkniecie Popovera przed nawigacja React Routera.
    // Bez tego React batchuje update razem z navigation, MUI renderuje Popover na nowej stronie
    // z nieaktualnym anchorEl i pozycjonuje go w prawym dolnym rogu.
    flushSync(() => {
      setPopoverAnchor(null);
    });
  }, []);

  // Gdy anchor zostanie odmontowany (np. ikona re-renderowana po refetch), zamknij popover
  // zamiast pozwolic MUI fallbackowac do pozycji (0,0) lub rogu viewportu.
  useEffect(() => {
    if (popoverAnchor && !popoverAnchor.isConnected) {
      setPopoverAnchor(null);
    }
  });

  // Zamykaj popover przy kazdej zmianie sciezki - po nawigacji layout sie zmienia
  // i MUI Popover nie przelicza pozycji wzgledem anchora, co powoduje przyklejenie sie do rogu.
  useEffect(() => {
    setPopoverAnchor(null);
  }, [pathname]);

  const renderCartContent = () => {
    return (
      <>
        <div className={styles.cartWrapper}>
          <div className={styles.cartHeader}>
            <ChevronLeft
              className={classnames(styles.arrow, { disabled: (currentCartIndex || 0) <= 0 })}
              onClick={prevCart}
            />
            {currentCart?.name}
            <ChevronRight
              className={classnames(styles.arrow, {
                disabled: (currentCartIndex || 0) >= (cartsData?.items.length || 0) - 1
              })}
              onClick={nextCart}
            />
          </div>
          <div className={styles.cartBody}>
            {currentCartId && (currentCart?.products_count || 0) > 0 ? (
              <CartPositions
                cartId={currentCartId}
                variant="wiseSmall"
                onChange={() => refetchCartsData()}
              />
            ) : (
              <div className={styles.emptyCart}>
                <span className={styles.title}>{t('Twój koszyk jest pusty')}</span>
                {t('Nie przegap okazji i sprawdź dostępne produkty')}!
              </div>
            )}
          </div>

          {(currentCart?.products_count || 0) > 0 && (
            <div className={styles.cartFooter}>
              <div className={styles.total}>
                <span className={styles.label}>{t('Suma')}:</span>
                <div className={styles.prices}>
                  <span className={styles.net}>
                    <span>
                      {currentCart?.positions_value_net_formatted} {currentCart?.currency}
                    </span>{' '}
                    {t('netto')}
                  </span>
                  <span className={styles.gross}>
                    <span>
                      {currentCart?.positions_value_gross_formatted} {currentCart?.currency}
                    </span>{' '}
                    {t('brutto')}
                  </span>
                </div>
              </div>

              <div className={styles.actions}>
                <Link to={`/cart/${currentCartId}`} onClick={handleClose}>
                  {t('Zobacz cały koszyk')}
                </Link>
                <Link
                  to={`/checkout/${currentCartId}`}
                  className={styles.primary}
                  onClick={handleClose}>
                  {t('Przejdź do kasy')}
                </Link>
              </div>
            </div>
          )}
        </div>
        {currentCartId && (currentCart?.products_count || 0) > 0 ? (
          <div className={styles.info}>
            <Availability
              stock={{
                name: (
                  <span className={styles.freeShipping}>
                    Darmowa dostawa od <strong>100zł</strong>
                  </span>
                ),
                value: 4,
                type: 'big-stock'
              }}
            />
          </div>
        ) : (
          <>
            <button
              className={styles.fromLastOrderButton}
              onClick={() => createCartFromLastOrder()}
              disabled={isCreatingFromLastOrder}>
              {isCreatingFromLastOrder
                ? t('Tworzenie koszyka...')
                : t('Utwórz koszyk na podstawie ostatniego zamówienia')}
            </button>
            <Link className={styles.info} to="/products">
              Zobacz produkty <ArrowRight />
            </Link>
          </>
        )}
      </>
    );
  };

  const renderFreeDeliveryContent = () => {
    if (currentCart?.has_free_delivery) {
      return (
        <div className={styles.freeDelivery}>
          <Trans>Darmowa dostawa!</Trans>
        </div>
      );
    }

    if (currentCart?.delivery_message) {
      return (
        <div className={classnames(styles.freeDelivery, styles.freeDeliveryMissing)}>
          <span>{currentCart?.delivery_message}</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Wise-WiseCartsButton'
      )}>
      <Badge
        color="secondary"
        badgeContent={0}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}>
        <div
          ref={buttonRef}
          className={classnames(styles.button, { [styles.active]: popoverAnchor })}
          onClick={handleOpen}>
          {isMobile ? (
            <div>
              <Cart className={styles.icon} />
            </div>
          ) : (
            <>
              <Cart className={styles.icon} />

              {currentCart && (
                <div className={styles.detailsCart}>
                  <div className={styles.countCarts}>{currentCart.name}</div>

                  <div className={styles.price}>
                    {currentCart.positions_value_net_formatted} {currentCart.currency}
                  </div>
                </div>
              )}
              <ChevronDown className={classnames('arrow', { rotated: !!popoverAnchor })} />
            </>
          )}
        </div>
        {!isMobile &&
          !popoverAnchor &&
          currentCart?.amount_to_free_delivery_formatted &&
          renderFreeDeliveryContent()}
      </Badge>
      {isMobile ? (
        !!popoverAnchor && (
          <Modal title={t('Koszyk')} onClose={handleClose} fullScreen>
            {renderCartContent()}
          </Modal>
        )
      ) : (
        // Conditional render calego Popovera - gdy popoverAnchor=null, MUI Popover
        // w ogole nie jest w React tree, wiec nie ma animacji exit ktora trzymalaby Paper
        // widoczny przez 200ms po nawigacji (powod buga z prawym dolnym rogiem).
        !!popoverAnchor && popoverAnchor.isConnected && (
          <Popover
            anchorEl={popoverAnchor}
            open
            onClose={handleClose}
            disableScrollLock
            transitionDuration={0}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            slotProps={{
              paper: {
                sx: {
                  marginTop: '8px',
                  borderRadius: '10px',
                  boxShadow: '0 12px 32px rgba(23, 62, 199, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06)',
                  overflow: 'hidden'
                }
              }
            }}>
            {renderCartContent()}
          </Popover>
        )
      )}
    </div>
  );
};

export default WiseCartsButton;
